<template>
  <Dialog :visible="manageQuotaDialogVisible"
          @update:visible="handleClose">
    <template #header>
      {{ manageQuotaId ? 'Edit' : 'Add' }} Quota
    </template>

    <div class="form-element">
      Quota Name:
      <InputText v-model="manageQuotaName" :invalid="manageQuotaNameInvalid" class="form-element full-width" type="text"
                 @change="manageQuotaNameChange"/>
      <div v-if="manageQuotaNameInvalid">
        <InlineMessage severity="error">Quota Name is required.</InlineMessage>
      </div>
    </div>
    <div class="form-element">
      Daily Limit:
      <InputText v-model="manageQuotaDailyLimit" :invalid="manageQuotaDailyLimitInvalid" class="form-element full-width" type="text"
                 @change="manageQuotaDailyLimitChange"/>
      <div v-if="manageQuotaDailyLimitInvalid">
        <InlineMessage severity="error">Daily Limit must be a valid integer.</InlineMessage>
      </div>
    </div>
    <div class="form-element">
      Monthly Limit:
      <InputText v-model="manageQuotaMonthlyLimit" :invalid="manageQuotaMonthlyLimitInvalid" class="form-element full-width" type="text"
                 @change="manageQuotaMonthlyLimitChange"/>
      <div v-if="manageQuotaMonthlyLimitInvalid">
        <InlineMessage severity="error">Monthly Limit must be a valid integer.</InlineMessage>
      </div>
    </div>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Cancel" severity="secondary" type="button" @click="handleClose"></Button>
        <Button :disabled="manageQuotaSubmitting" :loading="manageQuotaSubmitting" label="Save" type="button" @click="saveQuota"></Button>
      </div>
    </template>
  </Dialog>

  <div v-if="hasAdminAccess">
    <ProgressSpinnerContainer v-if="llmQuotasLoading" />
    <div v-else class="card">
      <div class="header-container mb flex">
        <h2 class="flex-1">LLM Quotas</h2>
        <div class="align-right mr">
          <Button @click="manageQuota(null)"><i class="pi pi-plus"></i> Add LLM Quota</Button>
        </div>
      </div>
      <ScrollPanel class="scroll-panel" style="max-height: 60vh">
        <DataTable :value="llmQuotas">
          <Column field="name" header="Name"></Column>
          <Column field="dailyQuota" header="Daily Limit"></Column>
          <Column field="monthlyQuota" header="Monthly Limit"></Column>
          <Column header="Actions">
            <template #body="slotProps">
              <Button v-if="slotProps.data.platformAccountId > 0" severity="info" @click="manageQuota(slotProps.data)" label="Edit"></Button>
              <Button v-if="slotProps.data.platformAccountId > 0" class="ml" severity="danger" @click="deleteLlmQuota(slotProps.data)" label="Delete"></Button>
            </template>
          </Column>
        </DataTable>
      </ScrollPanel>
    </div>
  </div>
  <div v-else>
    You do not have permission to access this page.
  </div>
</template>

<script setup>
import { useConfirm } from "primevue/useconfirm";
import ProgressSpinnerContainer from "../components/ProgressSpinnerContainer.vue";
import { useToast } from "primevue/usetoast";
import { ref, watch } from "vue";
import { useLlmQuotas, useUsers } from "../composables/entities.js";
import { selectedAccount } from "../store/state.js";
import { deleteRequest, patch, post } from "../util/api.js";
import { decodeLlmQuota } from "../util/util.js";

const { llmQuotas, llmQuotasLoading } = useLlmQuotas()

const { hasAdminAccess } = useUsers()

const toast = useToast()

const confirm = useConfirm()

async function deleteLlmQuota(data) {
  confirm.require({
    message: 'Are you sure you wish to delete ' + data.name + '?',
    header: 'Confirm Delete LLM Quota',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        llmQuotasLoading.value = true
        const request = await deleteRequest('/crbn-tech/ai-credit-quotas/' + data.id)
        if (request.id) {
          llmQuotas.value = llmQuotas.value.filter(q => String(q.id) !== String(request.id))
        }
        else {
          handleError()
        }
      }
      catch (e) {
        handleError(e)
      }
      finally {
        llmQuotasLoading.value = false
      }
      return true
    },
    reject: () => {
      return true
    }
  })
}

watch(llmQuotasLoading, (newValue)=> {
  if (!newValue) {
    if (!hasAdminAccess.value) {
      //router.replace('/')
    }
  }
}, { immediate: true })

const manageQuotaDialogVisible = ref(false)
const manageQuotaSubmitting = ref(false)

const manageQuotaId = ref(null)
const manageQuotaName = ref(null)
const manageQuotaDailyLimit = ref(null)
const manageQuotaMonthlyLimit = ref(null)

const manageQuotaNameInvalid = ref(false)
const manageQuotaDailyLimitInvalid = ref(false)
const manageQuotaMonthlyLimitInvalid = ref(false)

function manageQuotaNameChange() {
  manageQuotaNameInvalid.value = !manageQuotaName.value
}
function manageQuotaDailyLimitChange() {
  manageQuotaDailyLimitInvalid.value = false
}
function manageQuotaMonthlyLimitChange() {
  manageQuotaMonthlyLimitInvalid.value = false
}


function handleClose() {
  manageQuotaId.value = null
  manageQuotaName.value = null
  manageQuotaDailyLimit.value = null
  manageQuotaMonthlyLimit.value = null

  manageQuotaNameInvalid.value = null
  manageQuotaDailyLimitInvalid.value = null
  manageQuotaMonthlyLimitInvalid.value = null

  manageQuotaDialogVisible.value = false
  manageQuotaSubmitting.value = false
}

function manageQuota(data) {
  manageQuotaDialogVisible.value = true
  manageQuotaId.value = data?.id
  manageQuotaName.value = data?.name
  manageQuotaDailyLimit.value = data?.dailyQuota
  manageQuotaMonthlyLimit.value = data?.monthlyQuota
}

function handleError(e) {
  manageQuotaSubmitting.value = false
  toast.add({
    severity: 'error',
    group: 'mainToast',
    life: 5000,
    detail: 'An error has occurred.  Please try again.'
  })
}

function resetValidations() {
  manageQuotaNameInvalid.value = false
  manageQuotaDailyLimitInvalid.value = false
  manageQuotaMonthlyLimitInvalid.value = false
}

async function saveQuota() {
  let valid = true
  resetValidations()
  if (!manageQuotaName.value) {
    manageQuotaNameInvalid.value = true
    valid = false
  }
  /* If it contains a comma then make sure the commas are placed validly.
     If there are no commas, check for only digits. */
  if (manageQuotaDailyLimit.value) {
    if (String(manageQuotaDailyLimit.value).indexOf(',') > -1) {
      if (!(/^\d{1,3}(?:,\d{3})*$/.test(manageQuotaDailyLimit.value))) {
        manageQuotaDailyLimitInvalid.value = true
        valid = false
      }
    }
    else {
      if (!(/^\d*$/.test(manageQuotaDailyLimit.value))) {
        manageQuotaDailyLimitInvalid.value = true
        valid = false
      }
    }
  }
  if (manageQuotaMonthlyLimit.value) {
    if (String(manageQuotaMonthlyLimit.value).indexOf(',') > -1) {
      if (!(/^\d{1,3}(?:,\d{3})*$/.test(manageQuotaMonthlyLimit.value))) {
        manageQuotaMonthlyLimitInvalid.value = true
        valid = false
      }
    }
    else {
      if (!(/^\d*$/.test(manageQuotaMonthlyLimit.value))) {
        manageQuotaMonthlyLimitInvalid.value = true
        valid = false
      }
    }
  }

  if (!valid) {
    return false
  }

  if (manageQuotaId.value) {
    const payload = {
      name: manageQuotaName.value,
      daily_credit_quota: manageQuotaDailyLimit.value?.replace(/\D/g,''),
      monthly_credit_quota: manageQuotaMonthlyLimit.value?.replace(/\D/g,'')
    }
    manageQuotaSubmitting.value = true
    try {
      const result = await patch('/crbn-tech/ai-credit-quotas/' + manageQuotaId.value, payload)
      if (result.id) {
        for (let i = 0; i < llmQuotas.value.length; i++) {
          if (String(llmQuotas.value[i].id) === String(result.id)) {
            llmQuotas.value[i] = decodeLlmQuota(result)
          }
        }
      }
      else {
        handleError();
      }
      handleClose()
    }
    catch (e) {
      manageQuotaSubmitting.value = false
      handleError(e)
    }
  }
  else {
    const payload = {
      name: manageQuotaName.value,
      daily_credit_quota: manageQuotaDailyLimit.value?.replace(/\D/g,''),
      monthly_credit_quota: manageQuotaMonthlyLimit.value?.replace(/\D/g,''),
      cephisso_platform_accounts_id: selectedAccount.value
    }
    manageQuotaSubmitting.value = true
    try {
      const result = await post('/crbn-tech/ai-credit-quotas', payload)
      if (result.id) {
        llmQuotas.value.push(decodeLlmQuota(result))
      }
      else {
        handleError()
      }
      handleClose()
    }
    catch (e) {
      manageQuotaSubmitting.value = false
      handleError(e)
    }
  }
}

</script>

<style scoped>
</style>